import { NOT_APPLICABLE_SPACE_ID } from 'app/components/commons/Experience/PracticalInfo/SpacesSelect/SpacesSelect';
import { mapOpeningHours } from 'app/hooks/data/core/admin/experiences/mapping/form';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type {
  BuilderPriceDifferentiationMode,
  UpdateBuilderExperienceRequest,
} from 'app/typings/core/builder/experienceForm';
import type { BuilderHotelExperience } from 'app/typings/core/builder/experiences';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { dayjs, format, parse } from 'app/utils/dayjs';

import type {
  BuilderExperienceFormValues,
  BuilderPriceFormValues,
} from './types';

const toBuilderPriceDifferentiationMode = (
  mode?: string
): BuilderPriceDifferentiationMode =>
  mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
  mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
    ? PRICE_DIFFERENTIATION_MODE.WITH_CHILD
    : PRICE_DIFFERENTIATION_MODE.NONE;

const mapToPriceFormValues = (
  experience: BuilderHotelExperience
): BuilderPriceFormValues => {
  const priceConfiguration =
    experience.openingConfiguration?.priceConfiguration;

  const configuration =
    priceConfiguration?.type === OPENING_CONFIGURATION_TYPE.CONSTANT
      ? priceConfiguration.configuration
      : undefined;

  const hasAdult = experience.allowedPaxTypes.includes(PAX_TYPE.ADULT);

  const priceDifferentiationMode = hasAdult
    ? toBuilderPriceDifferentiationMode(
        priceConfiguration?.priceDifferentiationMode
      )
    : PRICE_DIFFERENTIATION_MODE.WITH_CHILD;

  const isFree = hasAdult
    ? priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.NONE &&
      configuration?.price === 0
    : configuration?.childPrice === 0;

  return {
    type: OPENING_CONFIGURATION_TYPE.CONSTANT,
    cost: isFree ? PRICE_COST_TYPE.FREE : PRICE_COST_TYPE.PAID,
    priceDifferentiationMode,
    configuration: {
      price: configuration?.price,
      childPrice: configuration?.childPrice,
    },
  };
};

export const mapExperienceToFormValues = (
  experience: BuilderHotelExperience
): BuilderExperienceFormValues => ({
  id: experience.id,
  hotelId: experience.hotelId,
  categoryId: experience.categoryId,
  allowedPaxTypes: experience.allowedPaxTypes,
  brand: experience.brand,
  duration: experience.duration,
  durationDescription: experience.durationDescription,
  time: experience.time
    ? parse(experience.time, 'HH:mm', new Date())
    : undefined,
  location: experience.location,
  description: experience.description,
  spaceIds: experience.spaceIds.length
    ? experience.spaceIds
    : [NOT_APPLICABLE_SPACE_ID],
  accessMode: experience.accessMode,
  openingHours: experience.openingHours,
  isRoomServiceAvailable: experience.isRoomServiceAvailable,
  isRoomServiceIncluded: experience.isRoomServiceIncluded,
  roomServicePrice: experience.roomServicePrice,
  experiencePictures: experience.experiencePictures,
  openingConfiguration: {
    priceConfiguration: mapToPriceFormValues(experience),
  },
});

const mapToPriceRequest = (
  priceConfiguration: BuilderPriceFormValues,
  allowedPaxTypes: string[]
): UpdateBuilderExperienceRequest['priceConfiguration'] => {
  const hasAdult = allowedPaxTypes.includes(PAX_TYPE.ADULT);
  const hasChild = allowedPaxTypes.includes(PAX_TYPE.CHILD);

  if (priceConfiguration.cost === PRICE_COST_TYPE.FREE) {
    return hasAdult
      ? {
          type: OPENING_CONFIGURATION_TYPE.CONSTANT,
          priceDifferentiationMode: PRICE_DIFFERENTIATION_MODE.NONE,
          configuration: { price: 0 },
        }
      : {
          type: OPENING_CONFIGURATION_TYPE.CONSTANT,
          priceDifferentiationMode: PRICE_DIFFERENTIATION_MODE.WITH_CHILD,
          configuration: { childPrice: 0 },
        };
  }

  const { configuration } = priceConfiguration;
  const priceDifferentiationMode = hasAdult
    ? priceConfiguration.priceDifferentiationMode
    : PRICE_DIFFERENTIATION_MODE.WITH_CHILD;
  const withChild =
    priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD;

  return {
    type: OPENING_CONFIGURATION_TYPE.CONSTANT,
    priceDifferentiationMode,
    configuration: {
      price: hasAdult ? configuration.price : undefined,
      childPrice: withChild && hasChild ? configuration.childPrice : undefined,
    },
  };
};

export const mapFormValuesToRequest = (
  values: BuilderExperienceFormValues,
  hasPrice: boolean
): UpdateBuilderExperienceRequest => ({
  description: values.description ?? null,
  brand: values.brand ?? null,
  duration: values.duration ?? null,
  durationDescription: values.duration ? values.durationDescription : null,
  time: values.time ? format(dayjs(values.time), 'HH:mm') : null,
  location: values.location ?? null,
  spaceIds:
    values.spaceIds?.filter((spaceId) => spaceId !== NOT_APPLICABLE_SPACE_ID) ??
    [],
  accessMode: values.accessMode ?? null,
  openingHours: mapOpeningHours(values.openingHours) ?? null,
  isRoomServiceAvailable: values.isRoomServiceAvailable || false,
  isRoomServiceIncluded: values.isRoomServiceIncluded || false,
  roomServicePrice: values.isRoomServiceIncluded
    ? null
    : (values.roomServicePrice ?? null),
  coverPicture: values.experiencePictures?.[0],
  experiencePictures: values.experiencePictures ?? [],
  priceConfiguration: hasPrice
    ? mapToPriceRequest(
        values.openingConfiguration.priceConfiguration,
        values.allowedPaxTypes
      )
    : undefined,
});
